import { createContext, useEffect, PropsWithChildren } from 'react';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import CurrentUser from 'src/types/CurrentUser';
import useAuth from 'src/api/auth/useAuth';
import useLogOut from 'src/api/auth/useLogOut';
import { useSwitchOrganization } from 'src/api/auth/useSwitchOrganization';
import { authzClient } from 'src/context/authz';
import { setNotify403IsAdmin } from 'src/api/notify403';

export interface AuthContextData {
  isFetchingAuthInitialStatus: boolean;
  currentUser?: CurrentUser;
  errorLoadingUser?: Error | unknown;
  refetchUser: (options?: RefetchOptions) => Promise<QueryObserverResult>;
  isRefetchingUser: boolean;
  logOut: () => Promise<void>;
  switchOrganization: (organizationId: string) => Promise<void>;
  isSwitchingOrganization: boolean;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

/** Fade the current page to white before a hard navigation. */
function fadeOutPage(durationMs = 400): Promise<void> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '9999',
      background: 'white',
      opacity: '0',
      transition: `opacity ${durationMs}ms ease-out`,
      pointerEvents: 'none',
    });
    document.body.appendChild(overlay);
    // Force layout so the browser registers opacity:0 before transitioning
    overlay.getBoundingClientRect();
    overlay.style.opacity = '1';
    const done = () => resolve();
    overlay.addEventListener('transitionend', done, { once: true });
    // Fallback in case transitionend never fires (reduced motion, backgrounded tab, etc.)
    setTimeout(done, durationMs + 100);
  });
}

const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const posthog = usePostHog();
  const {
    isFetchingAuthInitialStatus,
    currentUser,
    error: errorLoadingUser,
    isRefetching: isRefetchingUser,
    refetchUser,
  } = useAuth();

  const { mutateAsync: logOutApi } = useLogOut();
  const { mutateAsync: switchOrgApi, isPending: isSwitchingOrganization } =
    useSwitchOrganization();

  useEffect(() => {
    setNotify403IsAdmin(currentUser?.isAdmin ?? false);
  }, [currentUser?.isAdmin]);

  // Identify user in PostHog when currentUser is loaded
  useEffect(() => {
    if (currentUser && posthog) {
      // Set person properties for feature flag evaluation before reloading flags
      // https://posthog.com/docs/libraries/js#overriding-server-properties
      posthog.setPersonPropertiesForFlags({
        organizationId: currentUser.organizationId,
        ...(currentUser.tenantId && { tenantId: currentUser.tenantId }),
      });

      posthog.identify(currentUser.id, {
        email: currentUser.emails[0],
        name: currentUser.name,
        organizationId: currentUser.organizationId,
        ...(currentUser.tenantId && { tenantId: currentUser.tenantId }),
      });

      // Reload feature flags with the new person properties
      posthog.reloadFeatureFlags();
    }
  }, [currentUser, posthog]);

  const logOut = async () => {
    if (posthog) {
      posthog.reset();
    }
    // Reset authorization cache to prevent permission leakage between users
    authzClient.reset();
    await logOutApi();
    await refetchUser();
    await fadeOutPage();
    window.location.assign('/login');
  };

  const switchOrganization = async (organizationId: string) => {
    try {
      await switchOrgApi({ organizationId });
      // Reset authorization cache for new org's permissions
      authzClient.reset();
      // Full page reload to clear all cached data and get fresh state
      window.location.reload();
    } catch (error) {
      // Error already handled by oRPC's error handling
      // Don't reset authz or reload on failure
      console.error('Failed to switch organization:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isFetchingAuthInitialStatus,
        currentUser,
        errorLoadingUser,
        refetchUser,
        isRefetchingUser,
        logOut,
        switchOrganization,
        isSwitchingOrganization,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
